/* eslint-disable prettier/prettier */
/* eslint-disable no-nested-ternary */
import * as React from "react";
import {
  OutlinedInput,
  Box,
  Typography,
  TextareaAutosize,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import CardContent from "@mui/material/CardContent";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TagIcon from "@mui/icons-material/Tag";
import MenuItem from "@mui/material/MenuItem";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import Select from "@mui/material/Select";
import { useFeature } from "src/context/global";
import { useLocation, useNavigate } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import Button from "src/components/shared/atoms/button";
import useStyles from "./Styles";

const UpdateResponses = ({
  mode,
  handle,
  loading,
}: {
  id?: string | null;
  mode: string | null;
  handle: (a: any) => void;
  loading: boolean;
}) => {
  const { classes } = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { panelType } = useActiveUserDetail();
  const { enqueueSnackbar } = useSnackbar();

  const { canUse } = useFeature();
  const canSavePersonalCannedResponse = canUse(
    "saving_personal_canned_responses",
  );

  const canSaveAdminCannedResponse = canUse("saving_admin_canned_responses");

  // eslint-disable-next-line no-unsafe-optional-chaining
  const { response } = location?.state ?? {};
  const id = response?.id || null;
  const SUPERADMIN = "SUPERADMIN";

  const [initialFormikValues] = React.useState({
    text: response?.long || "",
    shortcut: response?.short || "",
    type:
      mode === "edit"
        ? response?.type
        : panelType === SUPERADMIN
        ? ""
        : "personal",
  });

  const validationSchema = Yup.object().shape({
    // type: Yup.string().required("Type of response is required"),
    type: Yup.string().test(
      "required-if-superadmin-and-add",
      "Type of response is required",
      function (value: string | undefined) {
        if (panelType === "SUPERADMIN" && mode === "add") {
          return !!value; // must have a value in add mode
        }
        return true; // edit mode or non-SUPERADMIN ? always valid
      },
    ),
    text: Yup.string()
      .required("Response text is required")
      .min(6, "Minimum 6 characters are required"),
    shortcut: Yup.string()
      .required("Shortcut is required")
      .min(3, "Minimum 3 characters are required")
      .max(60, "Must not exceed 60 characters"),
  });

  interface FormValuesProps {
    text: string;
    shortcut: string;
    type: string;
  }

  const updatedHandleSubmit = async (
    values: FormValuesProps,
    formikHelpers: any,
  ) => {
    try {
      const data = {
        id,
        type: values.type,
        long: values.text,
        short: values.shortcut,
      };
      handle(data);
    } catch (error) {
      enqueueSnackbar("Something went wrong.", {
        variant: "error",
      });
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };
  const formik = useFormik({
    initialValues: initialFormikValues,
    validationSchema,
    onSubmit: updatedHandleSubmit,
    enableReinitialize: true,
  });

  const handleCancel = () => {
    navigate("/settings/canned-response");
  };

  return (
    <Box>
      <Box className={classes.stickyHeader}>
        <Box className={classes.headerContainer}>
          <Box className={classes.box}>
            <Typography className={classes.boxText}>
              Canned Responses
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Tooltip title="Go Back">
              <IconButton
                onClick={handleCancel}
                className={classes.backButton}
                size="small"
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Chip
              icon={<TagIcon fontSize="small" />}
              label={mode === "edit" ? "Editing Response" : "Adding Response"}
              variant="outlined"
              className={classes.responseActionStyle}
            />
          </Box>
        </Box>
      </Box>

      <CardContent className={classes.cardContent}>
        <Box className={classes.bottomBox}>
          <Box component="form" onSubmit={formik.handleSubmit}>
            {panelType === SUPERADMIN && mode === "add" && (
              <>
                <Typography variant="body1" className={classes.typeText}>
                  Response Type
                </Typography>
                <Select
                  name="type"
                  value={formik.values.type}
                  className={classes.fieldStyle}
                  onChange={formik.handleChange}
                  displayEmpty
                  inputProps={{ "aria-label": "Without label" }}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {selected === "personal" && (
                        <>
                          <PersonIcon
                            color="primary"
                            sx={{ mr: 1.5, fontSize: "20px" }}
                          />
                          <Typography variant="body2" fontWeight={600}>
                            My Response
                          </Typography>
                        </>
                      )}
                      {selected === "generic" && (
                        <>
                          <GroupsIcon
                            color="primary"
                            sx={{ mr: 1.5, fontSize: "20px" }}
                          />
                          <Typography variant="body2" fontWeight={600}>
                            Generic Response
                          </Typography>
                        </>
                      )}
                      {!selected && (
                        <Typography variant="body2" sx={{ color: "#b2bec3" }}>
                          Select Type
                        </Typography>
                      )}
                    </Box>
                  )}
                >
                  {canSavePersonalCannedResponse && (
                    <MenuItem value="personal">
                      <PersonIcon
                        color="primary"
                        sx={{ mr: 1.5, fontSize: "20px" }}
                      />
                      <Typography variant="body2">My Response</Typography>
                    </MenuItem>
                  )}
                  {canSaveAdminCannedResponse && (
                    <MenuItem value="generic">
                      <GroupsIcon
                        color="primary"
                        sx={{ mr: 1.5, fontSize: "20px" }}
                      />
                      <Typography variant="body2">Generic Response</Typography>
                    </MenuItem>
                  )}
                </Select>
                <Box className={classes.formikText}>
                  {formik.touched.type && formik.errors.type && (
                    <FormHelperText error sx={{ fontWeight: 600 }}>
                      {formik.errors.type}
                    </FormHelperText>
                  )}
                </Box>
              </>
            )}

            <Typography
              variant="subtitle2"
              className={classes.typeText}
              sx={{ mt: panelType === SUPERADMIN && mode === "add" ? 3 : 0 }}
            >
              Response Text
            </Typography>

            <TextareaAutosize
              className={classes.textArea}
              name="text"
              placeholder="Type your response message here..."
              value={formik.values.text}
              onChange={formik.handleChange}
              minRows={5}
              maxRows={8}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  formik.submitForm();
                }
              }}
            />

            <Box className={classes.formikText}>
              {formik.touched.text && formik.errors.text && (
                <FormHelperText error sx={{ fontWeight: 600 }}>
                  {formik.errors.text}
                </FormHelperText>
              )}
            </Box>

            <Typography
              variant="subtitle2"
              className={classes.typeText}
              sx={{ mt: 3 }}
            >
              Shortcut Keyword
            </Typography>

            <OutlinedInput
              className={classes.fieldStyle}
              name="shortcut"
              placeholder="e.g. welcome"
              startAdornment={
                <Typography
                  sx={{
                    color: "#4D1277",
                    mr: 1,
                    fontWeight: 800,
                    fontSize: "18px",
                  }}
                >
                  #
                </Typography>
              }
              value={formik.values.shortcut}
              onChange={formik.handleChange}
              error={formik.touched.shortcut && Boolean(formik.errors.shortcut)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  formik.submitForm();
                }
              }}
            />
            <Box className={classes.formikText}>
              {formik.touched.shortcut && formik.errors.shortcut && (
                <FormHelperText error sx={{ fontWeight: 600 }}>
                  {formik.errors.shortcut}
                </FormHelperText>
              )}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
              <Button
                size="large"
                variant="contained"
                type="submit"
                className={classes.savebutton}
                circular={loading}
              >
                {mode === "edit" ? "Update Response" : "Save Response"}
              </Button>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Box>
  );
};

export default UpdateResponses;
